/**
 * Per-chart export (D529): the PNG of the drawn chart and the CSV of the plotted data, behind one small menu
 * (`ChartExportMenu`). No library: the PNG is the chart's SVG serialised with its computed styles inlined (the stylesheet
 * and the `var(--chart-*)` colours do not travel inside an SVG used as an image), drawn on a canvas.
 *
 * What a PNG holds: the SVG plot (marks, grid, axis text, value labels). It does not hold HTML pieces (the legend, the
 * tooltip), map tiles (an external raster would taint the canvas) or the web fonts (an SVG image cannot load them, so
 * the numbers fall back to the system monospace). The CSV is the exact plotted data, injection-safe (see `toCsv`).
 */
import { useEffect, useId, useRef, useState, type KeyboardEvent, type ReactElement } from 'react';
import { safeFileName, toCsv, type CsvCell } from '@vello/charts-core';
import { UiIcon } from '../icons';

/** The words of the menu (injected, localized). The library has no words of its own. */
export interface ChartExportLabels {
  /** The menu button: "Export". */
  readonly menu: string;
  readonly png: string;
  readonly csv: string;
}

/** The plotted data as a table: a header and rows. */
export interface ChartCsv {
  readonly header: readonly string[];
  readonly rows: readonly (readonly CsvCell[])[];
}

/** Props every chart accepts to offer an export. */
export interface ChartExportProps {
  /** Turns the export menu on (the words are injected). */
  readonly exportLabels?: ChartExportLabels;
  /** The file name stem (default: the title, else `chart`). */
  readonly exportName?: string;
}

const COPIED_STYLES = [
  'fill',
  'fill-opacity',
  'stroke',
  'stroke-width',
  'stroke-opacity',
  'stroke-dasharray',
  'stroke-linecap',
  'stroke-linejoin',
  'opacity',
  'font-family',
  'font-size',
  'font-weight',
  'text-anchor',
  'dominant-baseline',
  'shape-rendering',
  'visibility',
  'display',
] as const;

function inlineComputedStyles(source: Element, clone: Element): void {
  const computed = window.getComputedStyle(source);
  clone.setAttribute('style', COPIED_STYLES.map((p) => `${p}:${computed.getPropertyValue(p)}`).join(';'));
  const from = Array.from(source.children);
  const to = Array.from(clone.children);
  from.forEach((child, i) => {
    const twin = to[i];
    if (twin !== undefined) inlineComputedStyles(child, twin);
  });
}

/** The chart's SVG, standalone: styles inlined, tiles and screen-reader helpers removed, on a card-coloured background. */
export function standaloneSvg(svg: SVGSVGElement): { markup: string; width: number; height: number } {
  const rect = svg.getBoundingClientRect();
  const width = Math.max(1, Math.round(rect.width || Number(svg.getAttribute('width')) || 480));
  const height = Math.max(1, Math.round(rect.height || Number(svg.getAttribute('height')) || 240));
  const clone = svg.cloneNode(true) as SVGSVGElement;
  inlineComputedStyles(svg, clone);
  clone.querySelectorAll('image, .vello-chart__sr, [data-export="skip"]').forEach((n) => n.remove());
  clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
  clone.setAttribute('width', String(width));
  clone.setAttribute('height', String(height));
  clone.setAttribute('viewBox', `0 0 ${width} ${height}`);
  const card = window.getComputedStyle(document.documentElement).getPropertyValue('--color-surface-card').trim() || window.getComputedStyle(document.body).backgroundColor || 'none';
  const background = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
  background.setAttribute('width', String(width));
  background.setAttribute('height', String(height));
  background.setAttribute('fill', card);
  clone.insertBefore(background, clone.firstChild);
  return { markup: new XMLSerializer().serializeToString(clone), width, height };
}

/** Rasterise a chart SVG to a PNG blob (2× for a crisp image). `null` when the platform cannot (no canvas, blocked). */
export async function svgToPngBlob(svg: SVGSVGElement, scale = 2): Promise<Blob | null> {
  try {
    const { markup, width, height } = standaloneSvg(svg);
    const url = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(markup)}`;
    const image = new Image();
    image.decoding = 'async';
    await new Promise<void>((resolve, reject) => {
      image.onload = () => resolve();
      image.onerror = () => reject(new Error('svg-image'));
      image.src = url;
    });
    const canvas = document.createElement('canvas');
    canvas.width = width * scale;
    canvas.height = height * scale;
    const ctx = canvas.getContext('2d');
    if (ctx === null) return null;
    ctx.scale(scale, scale);
    ctx.drawImage(image, 0, 0, width, height);
    return await new Promise<Blob | null>((resolve) => canvas.toBlob((b) => resolve(b), 'image/png'));
  } catch {
    return null;
  }
}

/** Save a blob under a name through a temporary link. */
export function downloadBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** The CSV blob of a table. It carries a UTF-8 byte-order mark so a spreadsheet opens accents correctly. */
export function csvBlob(csv: ChartCsv): Blob {
  return new Blob(['﻿', toCsv(csv.header, csv.rows)], { type: 'text/csv;charset=utf-8' });
}

export interface ChartExportMenuProps {
  readonly labels: ChartExportLabels;
  /** The file name stem. */
  readonly name: string;
  /** The plotted data, built on demand. */
  readonly csv: () => ChartCsv;
  /** Offer the PNG (default true; a table has none). */
  readonly png?: boolean;
  /** Test seam: replaces the browser save. */
  readonly save?: (blob: Blob, fileName: string) => void;
}

/**
 * A small "Export" menu: PNG (the chart's SVG) and CSV (the plotted data). It sits in the chart's own toolbar row; the
 * button is a 48 px target, the items too; Escape closes it and returns the focus to the button.
 */
export function ChartExportMenu({ labels, name, csv, png = true, save = downloadBlob }: ChartExportMenuProps): ReactElement {
  const [open, setOpen] = useState(false);
  const wrapper = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const menuId = useId();
  const stem = safeFileName(name);

  useEffect(() => {
    if (!open) return undefined;
    wrapper.current?.querySelector<HTMLButtonElement>('[role="menuitem"]')?.focus();
    const onPointer = (event: PointerEvent): void => {
      if (wrapper.current !== null && event.target instanceof Node && !wrapper.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener('pointerdown', onPointer);
    return () => document.removeEventListener('pointerdown', onPointer);
  }, [open]);

  const close = (): void => {
    setOpen(false);
    trigger.current?.focus();
  };

  const onMenuKey = (event: KeyboardEvent<HTMLElement>): void => {
    if (event.key === 'Escape') {
      event.preventDefault();
      close();
      return;
    }
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    const items = Array.from(wrapper.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]') ?? []);
    if (items.length === 0) return;
    event.preventDefault();
    const at = items.findIndex((el) => el === document.activeElement);
    const next = event.key === 'ArrowDown' ? (at + 1) % items.length : (at - 1 + items.length) % items.length;
    items[next]?.focus();
  };

  const exportCsv = (): void => {
    save(csvBlob(csv()), `${stem}.csv`);
    close();
  };
  const exportPng = async (): Promise<void> => {
    const root = wrapper.current?.closest('.vello-chart');
    const svg = root?.querySelector<SVGSVGElement>('svg.vello-chart__svg') ?? null;
    close();
    if (svg === null) return;
    const blob = await svgToPngBlob(svg);
    if (blob !== null) save(blob, `${stem}.png`);
  };

  return (
    <div ref={wrapper} className="vello-chart__export" onKeyDown={onMenuKey} data-export="skip">
      <button ref={trigger} type="button" className="vello-chart__export-button" aria-haspopup="menu" aria-expanded={open} aria-controls={open ? menuId : undefined} onClick={() => setOpen((v) => !v)}>
        <UiIcon name="download" size={16} />
        <span>{labels.menu}</span>
      </button>
      {open && (
        <div id={menuId} role="menu" className="vello-chart__export-menu">
          {png && (
            <button type="button" role="menuitem" className="vello-chart__export-item" onClick={() => void exportPng()}>
              {labels.png}
            </button>
          )}
          <button type="button" role="menuitem" className="vello-chart__export-item" onClick={exportCsv}>
            {labels.csv}
          </button>
        </div>
      )}
    </div>
  );
}
